import type { ReactNode } from 'react';
import { cn } from '~/lib/utils';
import { ScrollRevealHeadline } from '~/landing_page/components/ScrollRevealHeadline';
import { ScrollRevealContent } from '~/landing_page/components/ScrollRevealContent';
import landingType from '~/landing_page/styles/landingTypography.module.css';
import feature1 from '~/landing_page/Assets/feature1.png';
import feature2 from '~/landing_page/Assets/feature2.png';
import feature3 from '~/landing_page/Assets/feature3.png';
import { FeatureAgentDemo } from './FeatureAgentDemo';
import { FeatureContextDemo } from './FeatureContextDemo';
import { FeaturePresentDemo } from './FeaturePresentDemo';
import { FeatureSearchDemo } from './FeatureSearchDemo';
import styles from './FeaturesSection.module.css';

export interface Feature {
  label?: string;
  title: string;
  description: string;
  imageSrc?: string;
  imageAlt?: string;
  imageSlot?: ReactNode;
  layout?: 'image-right' | 'image-left';
  visualBgSrc?: string;
}

const getDefaultFeatures = (): Feature[] => [
  {
    label: 'Agent',
    title: 'The agent that asks\nbefore it acts.',
    description:
      'Deck opens with questions, not assumptions. It understands what you are explaining and who you are talking to before building anything.',
    imageSrc: feature1,
    imageAlt: 'Chat agent with guided questions before building a deck',
    imageSlot: <FeatureAgentDemo />,
    layout: 'image-right',
    visualBgSrc: '/landing/features/agent_bg.jpg',
  },
  {
    label: 'Context',
    title: 'Your context, pulled\nfrom everywhere.',
    description:
      'Deck connects to the tools you already use, like Figma, Linear, and Notion, and pulls in the context it needs to build accurately.',
    imageSrc: feature2,
    imageAlt: 'Chat agent pulling designs and Linear issues for a sprint review deck',
    imageSlot: <FeatureContextDemo />,
    layout: 'image-left',
    visualBgSrc: '/landing/features/context_bg.jpg',
  },
  {
    label: 'Present',
    title: 'Edit it, shape it,\npresent it your way.',
    description:
      'Deck builds a complete canvas from your context, structured, editable, and ready to present. No more wrangling canvases from scratch.',
    imageSrc: feature3,
    imageAlt: 'Prompt bar generating an editable slide, then changing its layout',
    imageSlot: <FeaturePresentDemo />,
    layout: 'image-right',
    visualBgSrc: '/landing/features/visual_bg.jpg',
  },
  // New sub-section: the web search action. Copy and position are placeholder, set them with Guri.
  {
    label: 'Search',
    title: 'Turn on search and\nit looks things up.',
    description:
      'Flip on web search and Deck pulls live results as it works, so your story uses current facts and real sources.',
    imageAlt: 'Cursor turning on web search, then typing a research query into the prompt bar',
    imageSlot: <FeatureSearchDemo />,
    layout: 'image-left',
    visualBgSrc: '/landing/features/search_background.png',
  },
];

const DEFAULT_FEATURES: Feature[] = getDefaultFeatures();

export interface FeaturesSectionProps {
  id?: string;
  className?: string;
  headline?: string;
  features?: Feature[];
}

export function FeaturesSection({
  id,
  className,
  headline = 'Deck asks, finds, and builds.',
  features,
}: FeaturesSectionProps) {
  const resolvedFeatures = features ?? getDefaultFeatures();
  return (
    <section
      id={id}
      className={cn(landingType.sectionVerticalPadding, styles.section, className)}
      data-name="FeaturesSection"
    >
      <div className={cn(landingType.sectionInset, styles.container)}>
        <div className={styles.heading}>
          <ScrollRevealHeadline className={cn(landingType.sectionHeadline, styles.headline)}>
            {headline}
          </ScrollRevealHeadline>
        </div>

        <div className={styles.featuresContainer}>
          {resolvedFeatures.map((feature, index) => (
            <div
              key={`${feature.title}-${index}`}
              className={cn(
                styles.feature,
                feature.layout === 'image-left' && styles.featureReverse
              )}
            >
              <div className={styles.featureText}>
                <div className={styles.featureHeadingBlock}>
                  <ScrollRevealHeadline as="h3" className={cn(landingType.featureHeadline, styles.featureTitle)}>
                    {feature.title}
                  </ScrollRevealHeadline>
                </div>
                <ScrollRevealContent as="p" className={cn(landingType.sectionBody, styles.featureDescription)}>
                  {feature.description}
                </ScrollRevealContent>
              </div>

              <div className={styles.featureVisual}>
                <div
                  className={styles.featureVisualBg}
                  aria-hidden
                  style={
                    feature.visualBgSrc
                      ? { backgroundImage: `url(${feature.visualBgSrc})` }
                      : undefined
                  }
                />
                {feature.imageSlot ?? (
                  feature.imageSrc && (
                    <img
                      className={styles.featureImage}
                      src={feature.imageSrc}
                      alt={feature.imageAlt || ''}
                    />
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

FeaturesSection.displayName = 'FeaturesSection';
